import React, { useEffect, useState } from "react";
import {
  House,
  Drop,
  Car,
  CheckCircle,
  Plus,
  Minus,
} from "@phosphor-icons/react";
import { apiClient } from "../../lib/api";

const AC_TYPES = [
  { id: "none", label: "No AC", desc: "Trade winds only", baseCost: 0 },
  { id: "window", label: "Window units", desc: "1-3 wall units", baseCost: 119 },
  { id: "central", label: "Central AC", desc: "Whole-home", baseCost: 248 },
  { id: "split", label: "Mini-split", desc: "Heat pump", baseCost: 47 },
];

const WATER = [
  { id: "electric", label: "Electric tank" },
  { id: "solar", label: "Solar water" },
  { id: "gas", label: "Gas tankless" },
  { id: "heat_pump", label: "Heat pump" },
];

export default function StepHomeProfile({ value, onChange, onContinue, utilityProvider = "HECO" }) {
  const update = (k, v) => onChange({ ...value, [k]: v });
  const [breakdown, setBreakdown] = useState(null);

  // Live cost breakdown from backend
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await apiClient.post("/home/cost-breakdown", {
          sqft: value.sqft || 1800,
          occupants: value.occupants || 3,
          ac_type: value.ac_type,
          water_heater: value.water_heater,
          has_pool: value.has_pool,
          has_ev: value.has_ev,
          mini_split_count: value.mini_split_count || 0,
          utility_provider: utilityProvider,
        });
        if (!cancelled) setBreakdown(data);
      } catch (e) {
        // silent
      }
    })();
    return () => { cancelled = true; };
  }, [
    value.sqft, value.occupants, value.ac_type, value.water_heater,
    value.has_pool, value.has_ev, value.mini_split_count, utilityProvider,
  ]);

  // Find $/mo for ac/pool/ev
  const cost = (id) => {
    const item = breakdown?.items?.find((i) => i.id === id);
    return item ? `$${Math.round(item.monthly_cost)}/mo` : "—";
  };
  const acCost = (acId) => {
    if (acId === "none") return "$0/mo";
    if (acId === "split" && (value.mini_split_count || 0) > 0) {
      return value.ac_type === "split"
        ? cost("ac")
        : `~$${(110 * value.mini_split_count * 0.45).toFixed(0)}/mo`;
    }
    const map = { window: 127, central: 262, split: 99 };
    return `~$${map[acId] || 0}/mo`;
  };

  const totalLoadCost = breakdown?.items?.reduce((sum, i) => sum + i.monthly_cost, 0) || 0;

  return (
    <div data-testid="step-home" className="max-w-3xl mx-auto">
      <div className="text-center mb-10">
        <div className="inline-flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-full px-4 py-1.5 mb-5">
          <House size={14} weight="fill" className="text-amber-600" />
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-amber-700">
            About your home
          </span>
        </div>
        <h2 className="font-display text-4xl font-bold text-slate-950 tracking-tight">
          How does your home use energy?
        </h2>
        <p className="mt-3 text-slate-600">
          We'll show you what each load costs in $/month — that way you can spot where the biggest wins are.
        </p>
      </div>

      <div className="card-clean p-8 space-y-7">
        <div className="grid sm:grid-cols-2 gap-5">
          <label>
            <span className="block text-sm font-semibold text-slate-700 mb-2">Square footage</span>
            <input
              type="number"
              value={value.sqft}
              onChange={(e) => update("sqft", parseInt(e.target.value) || 0)}
              className="input-clean"
              data-testid="home-sqft-input"
              min="100"
              max="20000"
            />
          </label>
          <label>
            <span className="block text-sm font-semibold text-slate-700 mb-2">Occupants</span>
            <input
              type="number"
              value={value.occupants}
              onChange={(e) => update("occupants", parseInt(e.target.value) || 1)}
              className="input-clean"
              data-testid="home-occupants-input"
              min="1"
              max="20"
            />
          </label>
        </div>

        <div>
          <span className="block text-sm font-semibold text-slate-700 mb-3">Air Conditioning</span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {AC_TYPES.map((ac) => (
              <button
                key={ac.id}
                type="button"
                onClick={() => update("ac_type", ac.id)}
                data-testid={`ac-${ac.id}`}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                  value.ac_type === ac.id
                    ? "border-sky-500 bg-sky-50"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="font-semibold text-slate-900">{ac.label}</div>
                <div className="text-xs text-slate-500 mt-0.5">{ac.desc}</div>
                <div className="mt-2 text-xs font-bold text-amber-700 font-mono">{acCost(ac.id)}</div>
              </button>
            ))}
          </div>

          {value.ac_type === "split" && (
            <div data-testid="mini-split-counter" className="mt-4 flex items-center justify-between bg-sky-50 border border-sky-200 rounded-2xl p-4">
              <div>
                <div className="text-sm font-semibold text-slate-900">How many mini-split heads?</div>
                <div className="text-xs text-slate-600 mt-0.5">~$50/mo each on a typical Hawaii bill</div>
              </div>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  data-testid="mini-split-minus"
                  onClick={() => update("mini_split_count", Math.max(0, (value.mini_split_count || 0) - 1))}
                  className="w-9 h-9 rounded-full bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
                  aria-label="decrease"
                >
                  <Minus size={16} weight="bold" />
                </button>
                <span className="font-display font-bold text-2xl text-slate-950 w-8 text-center" data-testid="mini-split-count">
                  {value.mini_split_count || 0}
                </span>
                <button
                  type="button"
                  data-testid="mini-split-plus"
                  onClick={() => update("mini_split_count", Math.min(12, (value.mini_split_count || 0) + 1))}
                  className="w-9 h-9 rounded-full bg-sky-500 text-white hover:bg-sky-600 flex items-center justify-center"
                  aria-label="increase"
                >
                  <Plus size={16} weight="bold" />
                </button>
              </div>
            </div>
          )}
        </div>

        <div>
          <span className="block text-sm font-semibold text-slate-700 mb-3">Water Heater</span>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {WATER.map((w) => (
              <button
                key={w.id}
                type="button"
                onClick={() => update("water_heater", w.id)}
                data-testid={`water-${w.id}`}
                className={`text-left p-4 rounded-2xl border-2 transition-all ${
                  value.water_heater === w.id
                    ? "border-sky-500 bg-sky-50"
                    : "border-slate-200 hover:border-slate-300 bg-white"
                }`}
              >
                <div className="font-semibold text-slate-900">{w.label}</div>
              </button>
            ))}
          </div>
        </div>

        <div>
          <span className="block text-sm font-semibold text-slate-700 mb-3">Other big loads</span>
          <div className="grid sm:grid-cols-2 gap-3">
            <ToggleWithCost
              icon={Drop}
              label="Pool / Hot tub"
              cost={value.has_pool ? cost("pool") : "~$181/mo"}
              checked={value.has_pool}
              onChange={(v) => update("has_pool", v)}
              testid="has-pool-toggle"
            />
            <ToggleWithCost
              icon={Car}
              label="Electric vehicle"
              cost={value.has_ev ? cost("ev") : "~$158/mo"}
              checked={value.has_ev}
              onChange={(v) => update("has_ev", v)}
              testid="has-ev-toggle"
            />
          </div>
        </div>

        {/* Total load summary */}
        {breakdown && totalLoadCost > 0 && (
          <div data-testid="home-total-cost" className="rounded-2xl bg-gradient-to-r from-slate-950 to-slate-800 text-white p-5 flex items-center justify-between flex-wrap gap-3">
            <div>
              <div className="text-xs uppercase tracking-wider text-amber-300 font-bold">
                Major loads sum
              </div>
              <div className="text-xs text-slate-300 mt-0.5">
                AC + water + pool + EV
              </div>
            </div>
            <div className="text-right">
              <div className="font-display text-3xl font-bold text-white">
                ${Math.round(totalLoadCost)}<span className="text-lg text-slate-300">/mo</span>
              </div>
              <div className="text-xs text-amber-200 font-semibold">
                ${Math.round(totalLoadCost * 12).toLocaleString()}/year
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="mt-8 flex justify-center">
        <button data-testid="home-continue-btn" onClick={onContinue} className="btn-primary inline-flex items-center gap-2">
          Continue
        </button>
      </div>
    </div>
  );
}

function ToggleWithCost({ icon: Icon, label, cost, checked, onChange, testid }) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      data-testid={testid}
      className={`flex items-center gap-3 p-4 rounded-2xl border-2 transition-all ${
        checked
          ? "border-sky-500 bg-sky-50"
          : "border-slate-200 hover:border-slate-300 bg-white"
      }`}
    >
      <Icon size={22} weight="duotone" className={checked ? "text-sky-600" : "text-slate-500"} />
      <div className="flex-1 text-left">
        <div className="font-semibold text-slate-900">{label}</div>
        <div className="text-xs font-bold text-amber-700 font-mono mt-0.5">{cost}</div>
      </div>
      {checked && <CheckCircle size={20} weight="fill" className="text-sky-500" />}
    </button>
  );
}
